// src/components/InvoiceCancellationModal.jsx - SMART CANCELLATION WITH REASON-BASED HANDLING

import React, { useState } from 'react';
import { X, AlertTriangle, Loader, Info } from 'lucide-react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import StorageService from '../services/StorageService';

const InvoiceCancellationModal = ({ invoice, restaurantId, onClose, onCancelled }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  // Categorized cancellation reasons with smart handling
  const cancellationReasons = [
    {
      value: 'Printer Error',
      label: 'Printer Error',
      category: 'rebill', // Will rebill the order
      description: 'Technical issue - order will be available for rebilling'
    },
    {
      value: 'Technical Issue',
      label: 'Technical Issue / System Error',
      category: 'rebill',
      description: 'System problem - order will be available for rebilling'
    },
    {
      value: 'Customer Cancelled',
      label: 'Customer Cancelled Order',
      category: 'void', // Will mark order as cancelled, not rebill
      description: 'Customer decided not to proceed - order will be voided'
    },
    {
      value: 'Wrong Order',
      label: 'Wrong Order / Entry Error',
      category: 'void',
      description: 'Incorrect order entry - order will be voided'
    },
    {
      value: 'Duplicate Entry',
      label: 'Duplicate Invoice',
      category: 'void',
      description: 'Already billed elsewhere - order will be voided'
    },
    {
      value: 'Customer Paid Already',
      label: 'Customer Already Paid (Previous Invoice)',
      category: 'void',
      description: 'Payment received through another invoice - order will be voided'
    },
    {
      value: 'Test Invoice',
      label: 'Test / Training Invoice',
      category: 'void',
      description: 'Training or testing - order will be voided'
    },
    {
      value: 'Other',
      label: 'Other (Specify Below)',
      category: 'rebill', // Default to rebill for custom reasons
      description: 'Custom reason - order will be available for rebilling by default'
    }
  ];

  const selectedReasonData = cancellationReasons.find(r => r.value === reason);

  const handleCancel = async () => {
    const finalReason = reason === 'Other' ? customReason.trim() : reason;
    
    if (!finalReason) {
      setError('Please select or enter a cancellation reason');
      return;
    }

    if (reason === 'Other' && !customReason.trim()) {
      setError('Please provide a custom cancellation reason');
      return;
    }

    try {
      setCancelling(true);
      setError('');

      const invoiceRef = doc(
        db,
        'restaurants',
        restaurantId,
        'invoices',
        invoice.invoiceNumber
      );

      const now = new Date().toISOString();

      // Update invoice status
      await updateDoc(invoiceRef, {
        cancelled: true,
        cancellationReason: finalReason,
        cancelledAt: now,
        cancelledBy: 'User', // Replace with actual user from context if available
        status: 'Cancelled'
      });

      // Handle orders based on cancellation reason category
      if (invoice.orderIds && invoice.orderIds.length > 0) {
        const shouldRebill = selectedReasonData?.category === 'rebill';

        for (const orderId of invoice.orderIds) {
          try {
            const orderRef = doc(
              db,
              'restaurants',
              restaurantId,
              'orders',
              orderId
            );

            if (shouldRebill) {
              // REBILL CASE: Revert to unbilled (for printer errors, technical issues)
              await updateDoc(orderRef, {
                billed: false,
                invoiceNumber: null,
                billedAt: null,
                cancellationNote: `Previous invoice ${invoice.invoiceNumber} cancelled: ${finalReason}`
              });
            } else {
              // VOID CASE: Mark order as cancelled (for customer cancellations, wrong orders)
              await updateDoc(orderRef, {
                billed: true, // Keep as billed to avoid rebilling
                status: 'Cancelled', // Mark order as cancelled
                cancelled: true,
                invoiceNumber: invoice.invoiceNumber,
                cancellationReason: finalReason,
                cancelledAt: now,
                cancelledBy: 'User'
              });
            }
          } catch (orderError) {
            console.warn(`Could not update order ${orderId}:`, orderError);
          }
        }
      }

      StorageService.clearCache();
      
       
      
      const message = selectedReasonData?.category === 'rebill'
        ? '✅ Invoice cancelled! Orders are available for rebilling.'
        : '✅ Invoice cancelled! Orders have been voided and will not appear for rebilling.';
      
      alert(message);
      
      if (onCancelled) {
        onCancelled();
      }
      onClose();
    } catch (err) {
      console.error('❌ Error cancelling invoice:', err);
      setError('Failed to cancel invoice. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-red-200 bg-red-50 sticky top-0">
          <h2 className="text-xl font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            Cancel Invoice
          </h2>
          <button
            onClick={onClose}
            disabled={cancelling}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Invoice Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Invoice Number</p>
                <p className="font-bold text-gray-800">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-gray-600">Amount</p>
                <p className="font-bold text-orange-600">₹{invoice.grandTotal.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-600">Date</p>
                <p className="font-bold text-gray-800">{invoice.date}</p>
              </div>
              <div>
                <p className="text-gray-600">Customer</p>
                <p className="font-bold text-gray-800">{invoice.customerName}</p>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">⚠️ Warning:</span> Choose the cancellation reason carefully. It determines whether orders can be rebilled or will be permanently voided.
            </p>
          </div>

          {/* Cancellation Reason Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cancellation Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError('');
              }}
              disabled={cancelling}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select a reason...</option>
              {cancellationReasons.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            {/* Show description for selected reason */}
            {selectedReasonData && (
              <div className={`mt-3 p-3 rounded-lg border ${
                selectedReasonData.category === 'rebill' 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-start gap-2">
                  <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    selectedReasonData.category === 'rebill' 
                      ? 'text-blue-600' 
                      : 'text-purple-600'
                  }`} />
                  <div className="text-xs">
                    <p className={`font-semibold mb-1 ${
                      selectedReasonData.category === 'rebill' 
                        ? 'text-blue-800' 
                        : 'text-purple-800'
                    }`}>
                      {selectedReasonData.category === 'rebill' 
                        ? '🔄 Will Rebill Orders' 
                        : '❌ Will Void Orders'}
                    </p>
                    <p className={selectedReasonData.category === 'rebill' 
                      ? 'text-blue-700' 
                      : 'text-purple-700'
                    }>
                      {selectedReasonData.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Custom Reason Input */}
          {reason === 'Other' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Specify Cancellation Reason *
              </label>
              <textarea
                value={customReason}
                onChange={(e) => {
                  setCustomReason(e.target.value);
                  setError('');
                }}
                disabled={cancelling}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Enter detailed cancellation reason..."
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={cancelling}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition"
            >
              Keep Invoice
            </button>
            <button
              onClick={handleCancel}
              disabled={cancelling || !reason || (reason === 'Other' && !customReason.trim())}
              className="flex-1 bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
            >
              {cancelling ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            <p className="font-semibold mb-2">📋 Cancellation Policy:</p>
            <div className="space-y-1">
              <p><strong>🔄 Rebill Reasons:</strong> Printer Error, Technical Issues</p>
              <p className="text-blue-600 ml-4">→ Orders return to "Ready for Billing"</p>
              
              <p className="mt-2"><strong>❌ Void Reasons:</strong> Customer Cancelled, Wrong Order, Duplicate</p>
              <p className="text-purple-600 ml-4">→ Orders marked as cancelled (won't rebill)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCancellationModal;